const API_URL = '/api';

interface RequestOptions extends RequestInit {
  authorized?: boolean;
}

/**
 * Make a request to the API with proper token handling
 */
export const apiRequest = async <T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> => {
  const { authorized = true, ...requestOptions } = options;
  const url = `${API_URL}${endpoint}`;
  
  // Set up headers
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  
  // Add auth token for authorized requests
  if (authorized) {
    const storedSession = localStorage.getItem('userSession');
    if (storedSession) {
      const session = JSON.parse(storedSession);
      headers.set('Authorization', `Bearer ${session.token}`);
    }
  }

  try {
    // Make the request
    const response = await fetch(url, {
      ...requestOptions,
      headers,
    });

    // Handle errors
    if (!response.ok) {
      // Handle authentication errors (401 Unauthorized or 403 Forbidden)
      if (response.status === 401 || response.status === 403) {
        // Clear the invalid session
        localStorage.removeItem('userSession');
        
        // Force redirect to login page
        window.location.href = '/login';
        throw new Error('Authentication failed. Please log in again.');
      }
      
      const error = await response.text();
      throw new Error(error || `Request failed with status ${response.status}`);
    }

    // Parse and return the response
    if (response.status === 204) {
      return {} as T;
    }

    if (response.headers.get('Content-Type')?.includes('application/json')) {
      return await response.json() as T;
    }

    return await response.text() as unknown as T;
  } catch (error) {
    // Handle network errors (connection refused, server unreachable, etc.)
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      console.error('Backend connection failed:', error);
      
      // Clear any stored session since backend is unreachable
      localStorage.removeItem('userSession');
      
      // Redirect to login page immediately
      window.location.href = '/login';
      throw new Error('Unable to connect to server. Please check your connection and try again.');
    }
    
    // Handle other fetch errors (network timeouts, CORS, etc.)
    if (error instanceof Error && (
      error.message.includes('NetworkError') || 
      error.message.includes('Connection refused') ||
      error.message.includes('ECONNREFUSED') ||
      error.name === 'NetworkError'
    )) {
      console.error('Network error:', error);
      
      // Clear any stored session since backend is unreachable
      localStorage.removeItem('userSession');
      
      // Redirect to login page immediately
      window.location.href = '/login';
      throw new Error('Network connection failed. Please try again later.');
    }
    
    // Re-throw other errors
    throw error;
  }
}; 